import { SignInButton, useUser } from "@clerk/clerk-react";
import { Component, type ReactNode } from "react";
import { useConvexAuth, useQuery } from "convex/react";
import { Archive, Lock, LogIn } from "lucide-react";
import { api } from "../../convex/_generated/api";
import { isConfigured, serviceConfig } from "../config";
import { canManageWorkspaceActions, type WorkspaceMembershipSummary } from "../workspaceRolePermissions";
import { PanelHeading } from "./shared/ui";

export type ManagerAccessUser = {
  memberships?: Array<WorkspaceMembershipSummary & { organization?: { id: string; [key: string]: unknown } }>;
};

type ManagerAccessChildren = ReactNode | ((currentUser: ManagerAccessUser) => ReactNode);

export function ConvexAuthGate({ children, title }: { children: ReactNode; title: string }) {
  const { isLoaded, isSignedIn } = useUser();
  const convexAuth = useConvexAuth();

  if (!isLoaded || convexAuth.isLoading) {
    return (
      <section className="page-grid">
        <div className="empty-workflow-state">Loading {title.toLowerCase()}.</div>
      </section>
    );
  }

  if (!isSignedIn || !convexAuth.isAuthenticated) {
    return (
      <section className="page-grid">
        <div className="panel">
          <AccessPanelHeading title={title} />
          <div className="brief-actions">
            <SignInButton mode="modal">
              <button type="button">
                <LogIn size={16} />
                Sign in
              </button>
            </SignInButton>
          </div>
        </div>
      </section>
    );
  }

  return <>{children}</>;
}

export function ManagerAccessBoundary({ children }: { children: ManagerAccessChildren }) {
  if (!isConfigured(serviceConfig.convex.url)) {
    return <>{renderManagerAccessChildren(children, {})}</>;
  }

  return <ConfiguredManagerAccessBoundary>{children}</ConfiguredManagerAccessBoundary>;
}

function ConfiguredManagerAccessBoundary({ children }: { children: ManagerAccessChildren }) {
  const currentUser = useQuery(api.identity.getCurrentUserMemberships, {}) as ManagerAccessUser | null | undefined;

  if (currentUser === undefined) {
    return <AdminAccessPanel message="Checking admin access." />;
  }

  if (!canManageWorkspaceActions({ memberships: currentUser?.memberships })) {
    return <AdminAccessPanel message="Admin tools are available to account managers and agency admins." />;
  }

  return <>{renderManagerAccessChildren(children, currentUser ?? {})}</>;
}

function renderManagerAccessChildren(children: ManagerAccessChildren, currentUser: ManagerAccessUser) {
  return typeof children === "function" ? children(currentUser) : children;
}

export class FallbackBoundary extends Component<
  { children: JSX.Element; fallback: JSX.Element | ((error: Error) => JSX.Element) },
  { error?: Error }
> {
  state: { error?: Error } = {};

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  render() {
    if (!this.state.error) {
      return this.props.children;
    }

    return typeof this.props.fallback === "function" ? this.props.fallback(this.state.error) : this.props.fallback;
  }
}

export function WorkspaceAccessUnavailable({ title }: { title: string }) {
  return (
    <section className="page-grid">
      <div className="panel">
        <AccessPanelHeading title={title} />
        <div className="empty-workflow-state">
          This workspace needs a signed-in membership before Convex-backed data can be shown.
        </div>
      </div>
    </section>
  );
}

export function AdminAccessPanel({ message }: { message: string }) {
  return (
    <section className="page-grid">
      <section className="panel wide">
        <PanelHeading icon={<Archive />} title="Admin Access" />
        <p className="muted">{message}</p>
      </section>
    </section>
  );
}

function AccessPanelHeading({ title }: { title: string }) {
  return (
    <header className="panel-heading">
      <div>
        <Lock />
        <strong>{title}</strong>
      </div>
    </header>
  );
}
